import { resolveRedirects } from "@/actions/redirects/client/resolve-redirects"
import useSWR, { preload } from "swr"

const RESOLVE_PATH = "/api/redirects/resolve"
const KEY_SEPARATOR = "\n"

export type ResolveKey = readonly [typeof RESOLVE_PATH, string]

/**
 * The SWR key for a set of pathnames. Sorted and de-duplicated so the same set always
 * maps to the same key whatever order a caller lists it in; `null` disables the
 * request when there is nothing to resolve. Shared with `preloadResolvedRedirects` so a
 * hover-time prefetch and the panel's hook cannot drift onto two keys.
 */
export const buildResolveKey = (pathnames: readonly string[]): ResolveKey | null => {
    const unique = [...new Set(pathnames)].sort()
    return unique.length > 0 ? [RESOLVE_PATH, unique.join(KEY_SEPARATOR)] : null
}

const fetcher = ([, joined]: ResolveKey) => resolveRedirects(joined.split(KEY_SEPARATOR))

/**
 * The index behind the route changes at most a few times a day, and the picker asks
 * about the same paths every time it opens, so nothing revalidates on its own within a
 * browsing session. A preloaded entry is served on mount regardless of these flags; they
 * only stop later focus or reconnect events from re-fetching an unchanged answer.
 */
const RESOLVE_CONFIG = {
    dedupingInterval: 10 * 60 * 1000,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    keepPreviousData: true
} as const

/** Resolves `pathnames` through the server-side redirect index, one request per set. */
export const useSwrResolvedRedirects = (pathnames: readonly string[]) =>
    useSWR(buildResolveKey(pathnames), fetcher, RESOLVE_CONFIG)

/**
 * Warms the cache for `pathnames` before the panel that needs them is mounted. Called on
 * hover or focus of the picker trigger so the request runs while the user is still
 * deciding, not after the click. Safe to call repeatedly: `preload` is a no-op once the
 * key is cached or in flight.
 */
export const preloadResolvedRedirects = (pathnames: readonly string[]) => {
    const key = buildResolveKey(pathnames)
    if (key) void preload(key, fetcher)
}
